/**
 * Llamadas directas a la API REST de Strapi (sin @nuxtjs/strapi).
 * Compatible con Strapi v4/v5.
 */

interface StrapiQuery {
  locale?: string
  populate?: string[] | string
}

const getStrapiBaseUrl = (): string => {
  const config = useRuntimeConfig()
  if (import.meta.server) {
    return (config.public.STRAPI_SERVER_URL as string) || ''
  }
  return (config.public.STRAPI_CLIENT_URL as string) || ''
}

/**
 * Construye query string para Strapi (locale + populate[0]=x&populate[1]=y).
 */
const buildStrapiQueryString = (query: StrapiQuery): string => {
  const params = new URLSearchParams()
  if (query.locale) {
    params.set('locale', query.locale)
  }
  const populate = query.populate
  if (populate !== undefined) {
    const arr = Array.isArray(populate) ? populate : [populate]
    arr.forEach((value, i) => params.set(`populate[${i}]`, String(value)))
  }
  return params.toString()
}

/**
 * Obtiene contenido de Strapi por tipo (single type o collection).
*/
export const getStrapiData = async (url: string, query: StrapiQuery): Promise<any> => {
  const { locale } = useI18n()
  const localeValue = query.locale ?? locale.value
  let content: any

  try {
    const baseUrl = getStrapiBaseUrl()
    if (!baseUrl) {
      throw new Error('Strapi URL not configured')
    }

    const fullQuery = { ...query, locale: localeValue }
    const queryString = buildStrapiQueryString(fullQuery)
    const apiUrl = `${baseUrl.replace(/\/$/, '')}/api/${url}${queryString ? `?${queryString}` : ''}`

    const response: any = await $fetch(apiUrl)

    // Strapi REST: single type { data: {...} }, collection { data: [...] }
    content = response?.data ?? response
  } catch (error) {
    console.error('Error llamada Strapi', error)
    try {
      const contentData = await $fetch(`/strapi/${localeValue}/${url}`)
      content = extractStrapiData(Array.isArray(contentData) ? contentData[0] : contentData)
    } catch (fetchError) {
      console.error('Error llamada de respaldo', fetchError)
    }
  }

  return content
}

export const extractStrapiData = (response: any): any => {
  return response?.data
}

/**
 * Obtiene un documento de Strapi por filtro (ej. identifier, slug).
 */
export const getStrapiDataBy = async (
  url: string,
  by: string,
  queryValue: string,
  locale: string
): Promise<any> => {
  const config = useRuntimeConfig()
  const baseUrl = import.meta.server
    ? (config.public.STRAPI_SERVER_URL as string)
    : (config.public.STRAPI_CLIENT_URL as string)

  let content: any
  try {
    if (baseUrl) {
      const apiUrl = `${baseUrl.replace(/\/$/, '')}/api/${url}`
      const response: any = await $fetch(apiUrl, {
        query: {
          [`filters[${by}]`]: queryValue,
          locale
        }
      })
      content = response?.data?.[0] ?? response?.data
    } else {
      throw new Error('Strapi URL not configured')
    }
  } catch (error) {
    console.error('Error llamada Strapi', error)
    try {
      const contentData = await $fetch(`/strapi/${locale}/${url}`)
      const raw = Array.isArray(contentData) ? contentData[0] : contentData
      content = extractStrapiData(raw)
    } catch (fetchError) {
      console.error('Error llamada de respaldo', fetchError)
      throw new Error('No se pudo obtener los datos de Strapi')
    }
  }
  return content
}

export const getImageSrc = (strapiData: StrapiData, path: string): string => {
  const provider = strapiData?.provider
  const image = strapiData
  if (provider === 's3Provider') {
    return `${path}/${image.name}`
  }
  return image?.url ?? ''
}